import React, { useState, useMemo } from 'react';
import { BookModel, MemberModel, ScheduleEvent, BranchModel } from '../../../../models';
import {
  Calendar as CalendarIcon, User, Check, Search,
  ChevronLeft, ChevronRight, Trash2, GripVertical, X, Users,
  CheckCircle2, Circle, CalendarRange, Settings, ArrowRight, Plus
} from 'lucide-react';
import ScheduleSetting from './ScheduleSetting';
import AdminPageHeader from '../../frame/RightPanelHeader';

// 로컬 시간을 기준으로 YYYY-MM-DD 포맷을 생성하는 헬퍼 함수
export const formatLocalDate = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

interface ScheduleManagementProps {
  books: BookModel[];
  members: MemberModel[];
  branches: BranchModel[];
  schedules: ScheduleEvent[];
  onGenerate: (newSchedules: ScheduleEvent[]) => void;
  onUpdate: (id: string, updates: Partial<ScheduleEvent>) => void;
  onDelete: (id: string) => void;
  onBulkDelete: (ids: string[]) => void;
}

const ScheduleManagement: React.FC<ScheduleManagementProps> = ({
  books, members, branches, schedules, onGenerate, onUpdate, onDelete, onBulkDelete
}) => {
  // Selection State
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
  const [memberSearch, setMemberSearch] = useState('');

  // Range Selection State
  const [rangeSelection, setRangeSelection] = useState<{ start: Date | null; end: Date | null }>({ start: null, end: null });

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Calendar UI State
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [draggedEventId, setDraggedEventId] = useState<string | null>(null);

  const filteredMembers = members
    .filter(m => m.role === 'student')
    .filter(m =>
      m.name.toLowerCase().includes(memberSearch.toLowerCase()) ||
      m.grade.toLowerCase().includes(memberSearch.toLowerCase())
    );

  const toggleMember = (id: string) => {
    setSelectedMemberIds(prev =>
      prev.includes(id) ? prev.filter(mId => mId !== id) : [...prev, id]
    );
  };

  const selectAllMembers = () => {
    if (selectedMemberIds.length === filteredMembers.length) {
      setSelectedMemberIds([]);
    } else {
      setSelectedMemberIds(filteredMembers.map(m => m.id));
    }
  };

  // Calendar Logic
  const daysInMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();

  const calendarDays = useMemo(() => {
    const days = [];
    const totalDays = daysInMonth(currentMonth);
    const startOffset = firstDayOfMonth(currentMonth);
    for (let i = 0; i < startOffset; i++) days.push(null);
    for (let i = 1; i <= totalDays; i++) days.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i));
    return days;
  }, [currentMonth]);

  const changeMonth = (offset: number) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + offset, 1));
  };

  const getEventsForDate = (date: Date) => {
    if (selectedMemberIds.length === 0) return [];
    return schedules.filter(s => {
      if (!selectedMemberIds.includes(s.memberId)) return false;
      const sDate = new Date(s.date);
      return sDate.getFullYear() === date.getFullYear() &&
        sDate.getMonth() === date.getMonth() &&
        sDate.getDate() === date.getDate();
    });
  };

  const handleDateClick = (date: Date) => {
    if (!rangeSelection.start || (rangeSelection.start && rangeSelection.end)) {
      setRangeSelection({ start: date, end: null });
    } else {
      if (date < rangeSelection.start) {
        setRangeSelection({ start: date, end: null });
      } else {
        setRangeSelection({ start: rangeSelection.start, end: date });
      }
    }
  };

  const clearRangeSelection = () => {
    setRangeSelection({ start: null, end: null });
  };

  const getRangeDuration = () => {
    if (!rangeSelection.start || !rangeSelection.end) return 0;
    const diffTime = Math.abs(rangeSelection.end.getTime() - rangeSelection.start.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  const handleOpenScheduleModal = () => {
    if (selectedMemberIds.length === 0) {
      alert('일정을 생성할 학생을 먼저 선택해주세요.');
      return;
    }
    if (!rangeSelection.start || !rangeSelection.end) {
      alert('일정을 생성할 기간을 달력에서 선택해주세요.');
      return;
    }
    setIsModalOpen(true);
  };

  // 선택된 기간 일정 삭제
  const clearRangeSchedules = () => {
    if (selectedMemberIds.length === 0) {
      alert('일정을 삭제할 학생을 먼저 선택해주세요.');
      return;
    }
    if (!rangeSelection.start || !rangeSelection.end) {
      alert('삭제할 기간을 선택해주세요.');
      return;
    }

    // Set time to cover the full day
    const startDate = new Date(rangeSelection.start);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(rangeSelection.end);
    endDate.setHours(23, 59, 59, 999);

    const ids = schedules
      .filter(s => selectedMemberIds.includes(s.memberId))
      .filter(s => {
        const sTime = new Date(s.date).getTime();
        return sTime >= startDate.getTime() && sTime <= endDate.getTime();
      })
      .map(s => s.id);

    if (ids.length > 0) {
      if (confirm(`선택한 ${selectedMemberIds.length}명 학생의 해당 기간(${rangeSelection.start.toLocaleDateString()} ~ ${rangeSelection.end.toLocaleDateString()}) 일정을 모두 삭제하시겠습니까?`)) {
        onBulkDelete(ids);
        clearRangeSelection();
      }
    } else {
      alert('해당 기간에 삭제할 일정이 없습니다.');
    }
  };

  const clearWeek = (date: Date, e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedMemberIds.length === 0) {
      alert('일정을 삭제할 학생을 먼저 선택해주세요.');
      return;
    }
    const day = date.getDay();
    const start = new Date(date);
    start.setDate(date.getDate() - day);
    start.setHours(0, 0, 0, 0);

    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);

    const ids = schedules
      .filter(s => selectedMemberIds.includes(s.memberId))
      .filter(s => s.date >= start.getTime() && s.date <= end.getTime())
      .map(s => s.id);

    if (ids.length > 0) {
      if (confirm(`선택한 ${selectedMemberIds.length}명 학생의 해당 주 일정을 모두 삭제하시겠습니까?`)) {
        onBulkDelete(ids);
      }
    } else {
      alert('해당 주에 삭제할 일정이 없습니다.');
    }
  };

  const onDragStart = (id: string) => setDraggedEventId(id);
  const onDrop = (date: Date) => {
    if (!draggedEventId) return;
    onUpdate(draggedEventId, { date: date.getTime() });
    setDraggedEventId(null);
  };

  const toggleStatus = (event: ScheduleEvent) => {
    const nextStatus = event.status === 'completed' ? 'pending' : 'completed';
    onUpdate(event.id, { status: nextStatus });
  };

  const editTask = (event: ScheduleEvent) => {
    const newTask = prompt('일정 내용을 수정하세요:', event.task);
    if (newTask !== null && newTask.trim() !== '') {
      onUpdate(event.id, { task: newTask.trim() });
    }
  };

  const getSubjectLightColor = (subject: string) => {
    const colors: Record<string, string> = {
      '국어': 'text-rose-600', '수학': 'text-blue-600', '영어': 'text-indigo-600',
      '한국사': 'text-amber-700', '사회': 'text-orange-600', '과학': 'text-emerald-600',
    };
    return colors[subject] || 'text-slate-600';
  };

  return (
    <div className="flex flex-col h-full bg-white overflow-hidden relative">
      <AdminPageHeader
        title="일정 관리"
        icon={CalendarIcon}
        subtitle="학생별 학습 일정을 생성하고 관리합니다"
      />

      <div className="flex-1 h-full flex gap-4 overflow-hidden p-6 bg-slate-50 relative">

        {/* 1. 학생 목록 패널 */}
        <section className="w-64 bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col overflow-hidden shrink-0">
          <div className="p-4 border-b border-slate-100 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-black text-slate-800 flex items-center gap-2 uppercase tracking-widest">
                <User size={14} className="text-indigo-600" /> 학생 선택 ({selectedMemberIds.length})
              </h3>
              <button
                onClick={selectAllMembers}
                className="text-[10px] font-bold text-indigo-600 hover:underline"
              >
                {selectedMemberIds.length === filteredMembers.length ? '전체 해제' : '전체 선택'}
              </button>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={12} />
              <input
                type="text" placeholder="학생 검색..." value={memberSearch} onChange={e => setMemberSearch(e.target.value)}
                className="w-full bg-slate-50 border-none rounded-xl pl-8 pr-4 py-2 text-[11px] focus:ring-2 focus:ring-indigo-500 outline-none"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-2 custom-scrollbar">
            {filteredMembers.map(m => {
              const branchName = branches.find(b => b.id === m.branchId)?.name || '-';
              return (
                <button
                  key={m.id} onClick={() => toggleMember(m.id)}
                  className={`w-full flex items-center gap-2 p-2.5 rounded-xl text-left transition-all border mb-1 ${selectedMemberIds.includes(m.id) ? 'bg-indigo-50 border-indigo-100 shadow-sm' : 'border-transparent hover:bg-slate-50'
                    }`}
                >
                  <div className={`w-4 h-4 rounded-sm flex items-center justify-center border transition-all shrink-0 ${selectedMemberIds.includes(m.id) ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white border-slate-300'
                    }`}>
                    {selectedMemberIds.includes(m.id) && <Check size={10} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className="text-[11px] font-black text-slate-800 truncate leading-none">{m.name}</span>
                      <span className="text-[9px] text-indigo-500 font-bold truncate leading-none bg-indigo-50 px-1 py-0.5 rounded">{branchName}</span>
                    </div>
                    <div className="flex items-center text-[9px] text-slate-400 font-medium truncate leading-none gap-1">
                      <span>{m.schoolName || '-'}</span>
                      <span className="w-0.5 h-0.5 bg-slate-300 rounded-full"></span>
                      <span>{m.grade}</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {/* 2. 달력 패널 */}
        <section className="flex-1 bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col overflow-hidden">
          <div className="p-4 bg-white border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h2 className="text-lg font-black text-slate-800 flex items-center gap-2">
                <CalendarIcon size={20} className="text-indigo-600" />
                {currentMonth.getFullYear()}년 {currentMonth.getMonth() + 1}월
              </h2>
              <div className="flex bg-slate-100 p-0.5 rounded-lg scale-90">
                <button onClick={() => changeMonth(-1)} className="p-1.5 hover:bg-white hover:shadow-sm rounded-md transition-all"><ChevronLeft size={16} /></button>
                <button onClick={() => setCurrentMonth(new Date())} className="px-2 text-[10px] font-bold text-slate-500">오늘</button>
                <button onClick={() => changeMonth(1)} className="p-1.5 hover:bg-white hover:shadow-sm rounded-md transition-all"><ChevronRight size={16} /></button>
              </div>
              {selectedMemberIds.length > 0 && (
                <div className="hidden lg:flex items-center gap-1 px-3 py-1 bg-indigo-50 border border-indigo-100 rounded-full">
                  <Users size={12} className="text-indigo-500" />
                  <span className="text-[10px] font-bold text-indigo-700">
                    {selectedMemberIds.length}명의 일정 관리 중
                  </span>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              {/* Old delete buttons removed */}
            </div>
          </div>
          <div className="grid grid-cols-7 border-b border-slate-50 bg-slate-50/50">
            {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map((d, i) => (
              <div key={d} className={`py-2 text-center text-[9px] font-black tracking-widest ${i === 0 ? 'text-rose-400' : i === 6 ? 'text-indigo-400' : 'text-slate-400'}`}>
                {d}
              </div>
            ))}
          </div>
          <div className="flex-1 overflow-hidden grid grid-cols-7 auto-rows-fr bg-slate-200 gap-[1px]">
            {calendarDays.map((day, idx) => {
              if (!day) return <div key={`empty-${idx}`} className="bg-slate-50/30"></div>;
              const dayEvents = getEventsForDate(day);
              const isToday = new Date().toDateString() === day.toDateString();

              // Range Logic matching Student UI
              const isRangeStart = rangeSelection.start && day.toDateString() === rangeSelection.start.toDateString();
              const isRangeEnd = rangeSelection.end && day.toDateString() === rangeSelection.end.toDateString();
              const isInRange = rangeSelection.start && rangeSelection.end && day > rangeSelection.start && day < rangeSelection.end;

              let containerStyle = "bg-white hover:bg-slate-50";
              if (isInRange) containerStyle = "bg-indigo-50/50";
              if (isRangeStart || isRangeEnd) containerStyle = "bg-indigo-50 ring-2 ring-inset ring-indigo-500 z-10";

              return (
                <div
                  key={day.toISOString()}
                  onClick={() => handleDateClick(day)}
                  onDragOver={e => e.preventDefault()}
                  onDrop={() => onDrop(day)}
                  className={`relative group flex flex-col p-1.5 min-h-0 overflow-hidden cursor-pointer ${containerStyle} ${isToday && !isRangeStart && !isRangeEnd ? 'bg-indigo-50/10' : ''}`}
                >
                  <div className="flex justify-between items-start mb-1">
                    <span className={`text-[10px] font-black ${isRangeStart || isRangeEnd ? 'bg-indigo-600 text-white w-4 h-4 flex items-center justify-center rounded shadow-sm' :
                      isToday ? 'bg-rose-500 text-white w-4 h-4 flex items-center justify-center rounded shadow-sm' :
                        (day.getDay() === 0 ? 'text-rose-500' : day.getDay() === 6 ? 'text-indigo-500' : 'text-slate-400')
                      }`}>
                      {day.getDate()}
                    </span>
                    <button onClick={(e) => clearWeek(day, e)} title="해당 주의 모든 일정 삭제" className="opacity-0 group-hover:opacity-100 p-0.5 text-slate-300 hover:text-rose-500 transition-all"><X size={10} /></button>
                  </div>
                  <div className="flex-1 space-y-1 overflow-y-auto custom-scrollbar">
                    {dayEvents.map(e => (
                      <div
                        key={e.id} draggable onDragStart={() => onDragStart(e.id)}
                        onClick={(ev) => ev.stopPropagation()}
                        className={`group/item relative flex flex-col p-1.5 rounded-lg border transition-all cursor-move ${e.status === 'completed'
                          ? 'bg-emerald-50 border-emerald-100 text-emerald-700 opacity-80'
                          : 'bg-white border-slate-100 shadow-sm hover:border-indigo-200'
                          }`}
                      >
                        <div className="flex items-center gap-1 mb-1">
                          <GripVertical size={8} className="text-slate-300 shrink-0" />
                          <div className="flex-1 min-w-0 truncate text-[9px] font-bold">
                            <span className={getSubjectLightColor(e.subject)}>[{e.subject}]</span> {e.bookTitle}
                          </div>
                        </div>

                        <div className="flex items-center justify-between gap-1">
                          <span onClick={() => editTask(e)} className="text-[10px] font-black text-slate-800 truncate cursor-pointer hover:underline">
                            {e.task}
                          </span>
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              onClick={() => toggleStatus(e)}
                              className={`transition-colors ${e.status === 'completed' ? 'text-emerald-600' : 'text-slate-300 hover:text-indigo-500'}`}
                              title={e.status === 'completed' ? '미완료로 표시' : '완료로 표시'}
                            >
                              {e.status === 'completed' ? <CheckCircle2 size={11} /> : <Circle size={11} />}
                            </button>
                            <button
                              onClick={() => onDelete(e.id)}
                              className="text-slate-300 hover:text-rose-500 opacity-0 group-hover/item:opacity-100"
                              title="일정 삭제"
                            >
                              <X size={11} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Range Selection Bar - Now Integrated at the bottom */}
          {rangeSelection.start && rangeSelection.end && (
            <div className="bg-slate-900 text-white p-5 border-t border-slate-700 flex items-center justify-between animate-in slide-in-from-bottom-full duration-300 shrink-0">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center text-indigo-400">
                  <CalendarRange size={20} />
                </div>
                <div>
                  <div className="flex items-center gap-2 text-sm font-black whitespace-nowrap">
                    <span>{rangeSelection.start.toLocaleDateString()}</span>
                    <ArrowRight size={14} className="text-slate-500" />
                    <span>{rangeSelection.end.toLocaleDateString()}</span>
                  </div>
                  <p className="text-[10px] text-slate-400 font-bold whitespace-nowrap mt-0.5">
                    총 {getRangeDuration()}일 선택됨 • {selectedMemberIds.length}명 학생 대상
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={clearRangeSchedules}
                  className="bg-rose-600 hover:bg-rose-500 text-white px-7 py-2.5 rounded-full text-sm font-black shadow-lg shadow-rose-500/20 transition-all flex items-center gap-2 whitespace-nowrap active:scale-95"
                >
                  <Trash2 size={16} strokeWidth={3} />
                  선택 삭제
                </button>
                <button
                  onClick={handleOpenScheduleModal}
                  className="bg-indigo-600 hover:bg-indigo-500 text-white px-7 py-2.5 rounded-full text-sm font-black shadow-lg shadow-indigo-500/20 transition-all flex items-center gap-2 whitespace-nowrap active:scale-95"
                >
                  <Plus size={18} strokeWidth={3} />
                  일정 생성
                </button>
                <button
                  onClick={clearRangeSelection}
                  className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-slate-800 text-slate-500 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
          )}
        </section>

        {/* Schedule Rule Modal */}
        {isModalOpen && (
          <ScheduleSetting
            book={null} // Pass null to trigger book selection inside modal
            selectedMemberIds={selectedMemberIds}
            members={members}
            branches={branches}
            books={books} // Pass all books for selection
            startDate={rangeSelection.start ? formatLocalDate(rangeSelection.start) : undefined}
            endDate={rangeSelection.end ? formatLocalDate(rangeSelection.end) : undefined}
            onClose={() => { setIsModalOpen(false); }}
            onGenerate={(events) => {
              onGenerate(events);
              setIsModalOpen(false);
              clearRangeSelection();
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ScheduleManagement;
